import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { createUser } from '../services/userService';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import UserAgreementModal from '../components/UserAgreementModal';
import RecaptchaComponent from '../components/RecaptchaComponent';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

const Signup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState('');
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword || !formData.displayName) {
      setError('All fields are required');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!agreementAccepted) {
      setError('You must accept the User Agreement to continue');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Execute reCAPTCHA verification
      if (recaptchaRef.current && recaptchaRef.current.executeRecaptcha) {
        const token = await recaptchaRef.current.executeRecaptcha();
        if (!token) {
          setError('reCAPTCHA verification failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );

      const user = userCredential.user;

      // Update display name
      await updateProfile(user, {
        displayName: formData.displayName
      });

      // Create user document in Firestore
      await createUser(user.uid, formData.email, {
        displayName: formData.displayName,
        emailVerified: false
      });

      // Send email verification
      await sendEmailVerification(user);

      // Success - redirect to email verification page
      navigate('/verify-email', { 
        state: { 
          email: formData.email,
          displayName: formData.displayName 
        } 
      });
      
    } catch (error) {
      console.error('Signup error:', error);
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please choose a stronger password');
          break;
        default:
          setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementAccept = () => {
    setAgreementAccepted(true);
    setShowAgreement(false);
  };

  const handleAgreementDecline = () => {
    setAgreementAccepted(false);
    setShowAgreement(false);
  };

  const handleRecaptchaVerify = (token) => {
    setRecaptchaToken(token);
    setRecaptchaError('');
    // Clear any existing error when reCAPTCHA is verified
    if (error && error.includes('reCAPTCHA')) {
      setError('');
    }
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaError(error);
    setRecaptchaToken(null);
  };

  return (
    <div className="container mt-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card>
            <Card.Header className="text-center">
              <h3>Create Account</h3>
              <p className="text-muted mb-0">Join PulseSync today</p>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a password (min 6 characters)"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="agreement-check"
                    label={
                      <span>
                        I agree to the{' '}
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => setShowAgreement(true)}
                        >
                          User Agreement & Terms of Service
                        </Button>
                      </span>
                    }
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <RecaptchaComponent
                    ref={recaptchaRef}
                    onVerify={handleRecaptchaVerify}
                    onError={handleRecaptchaError}
                    siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  />
                  {recaptchaError && (
                    <div className="text-danger small mt-1">
                      {recaptchaError}
                    </div>
                  )}
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mb-3"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </Form>

              <div className="text-center">
                <p className="mb-0">
                  Already have an account?{' '}
                  <Link to="/login" className="text-decoration-none">
                    Sign in here
                  </Link>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <UserAgreementModal
        show={showAgreement}
        onAccept={handleAgreementAccept}
        onDecline={handleAgreementDecline}
      />
    </div>
  );
};

const SignupWithRecaptcha = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  if (!recaptchaSiteKey) {
    console.warn('reCAPTCHA site key not configured');
    return <Signup />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <Signup />
    </GoogleReCaptchaProvider>
  );
};

export default SignupWithRecaptcha;
